/**
 * Scales: map a data domain to a pixel range. A linear scale for values and positions in time, a band scale for
 * categories (bars). Both are plain closures over numbers — no DOM, no framework.
 */
import { clamp, isFiniteNumber } from './numbers.js';
/**
 * A linear scale. A degenerate domain (min === max) maps every value to the MIDDLE of the range instead of dividing by
 * zero, so a flat series draws a centred line rather than NaN.
 */
export function linearScale(domain, range, options = {}) {
    const [d0, d1] = domain;
    const [r0, r1] = range;
    const span = d1 - d0;
    const flat = span === 0 || !isFiniteNumber(span);
    const lo = Math.min(r0, r1);
    const hi = Math.max(r0, r1);
    const scale = ((value) => {
        if (flat)
            return (r0 + r1) / 2;
        const out = r0 + ((value - d0) / span) * (r1 - r0);
        return options.clamp === true ? clamp(out, lo, hi) : out;
    });
    const invert = (position) => {
        if (flat || r1 === r0)
            return d0;
        const v = d0 + ((position - r0) / (r1 - r0)) * span;
        return options.clamp === true ? clamp(v, Math.min(d0, d1), Math.max(d0, d1)) : v;
    };
    return Object.assign(scale, { domain: [d0, d1], range: [r0, r1], invert });
}
/** A band scale for `count` categories across `range`. Zero bands is valid (an empty chart): every method is safe. */
export function bandScale(count, range, options = {}) {
    const n = Math.max(0, Math.floor(isFiniteNumber(count) ? count : 0));
    const padding = clamp(options.padding ?? 0.25, 0, 0.9);
    const outer = clamp(options.paddingOuter ?? padding / 2, 0, 0.9);
    const [r0, r1] = range;
    const width = r1 - r0;
    // n bands + (n − 1) inner gaps of `padding` steps + two outer gaps of `outer` steps = the width.
    const stepUnits = n === 0 ? 1 : n - padding + 2 * outer;
    const step = n === 0 ? 0 : width / Math.max(stepUnits, 1e-9);
    const bandwidth = step * (1 - padding);
    const origin = r0 + step * outer;
    const start = (index) => origin + index * step;
    const indexAt = (position) => {
        if (n === 0 || !isFiniteNumber(position))
            return -1;
        if (position < Math.min(r0, r1) || position > Math.max(r0, r1))
            return -1;
        return clamp(Math.floor((position - origin + (step * padding) / 2) / step), 0, n - 1);
    };
    return { count: n, bandwidth, step, start, center: (index) => start(index) + bandwidth / 2, indexAt };
}
/** Evenly spaced positions for `count` points across `range`, first at the start, last at the end (a line chart's x). */
export function pointPositions(count, range) {
    const n = Math.max(0, Math.floor(isFiniteNumber(count) ? count : 0));
    if (n === 0)
        return [];
    if (n === 1)
        return [(range[0] + range[1]) / 2];
    const out = [];
    for (let i = 0; i < n; i += 1)
        out.push(range[0] + ((range[1] - range[0]) * i) / (n - 1));
    return out;
}
/** The index of the position nearest to `position` in an ascending list of x positions, or −1 when the list is empty. */
export function nearestIndex(positions, position) {
    if (positions.length === 0 || !isFiniteNumber(position))
        return -1;
    let best = 0;
    let bestDistance = Infinity;
    positions.forEach((p, i) => {
        const d = Math.abs(p - position);
        if (d < bestDistance) {
            bestDistance = d;
            best = i;
        }
    });
    return best;
}
//# sourceMappingURL=scale.js.map